import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid, Input, Button, Dropdown,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';

// Product selection + VIN entry; triggers the transaction lookup.
function VinSearchForm({
  loading, disabled, products, productsLoading, searchProductCode, searchVin, onSearch,
}) {
  const [productCode, setProductCode] = useState(searchProductCode);
  const [vin, setVin] = useState(searchVin);

  // Typing is kept local so every keystroke does not invalidate the flow, but
  // the submitted query in the store stays authoritative: when it changes from
  // the outside (Start New Request resets it to empty) the fields follow it.
  useEffect(() => {
    setProductCode(searchProductCode);
    setVin(searchVin);
  }, [searchProductCode, searchVin]);

  // Once the products are loaded, preselect the first one if nothing is chosen yet.
  useEffect(() => {
    if (isEmpty(productCode) && !isEmpty(products)) {
      setProductCode(products[0].productCode);
    }
  }, [products, productCode]);

  const productOptions = products.map(p => ({
    key: p.productCode,
    text: p.displayName,
    description: p.description,
    value: p.productCode,
  }));

  const handleProductChange = (e, { value }) => {
    setProductCode(value);
  };

  const handleChange = (e, { value }) => {
    setVin(value);
  };

  const searchDisabled = disabled || isEmpty(productCode) || isEmpty(vin.trim());

  const handleSearch = () => {
    if (searchDisabled) return;
    onSearch(productCode, vin.trim());
  };

  return (
    <Grid container columns={2}>
      <Grid.Row>
        <Grid.Column width={3} verticalAlign='middle'>
          <b>Product</b>
        </Grid.Column>
        <Grid.Column width={9}>
          <Dropdown
            fluid
            selection
            search
            placeholder='Select a product'
            options={productOptions}
            value={productCode}
            onChange={handleProductChange}
            loading={productsLoading}
            disabled={disabled || loading || productsLoading}
            data-testid='force-guarantee-product-dropdown'
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3} verticalAlign='middle'>
          <b>VIN</b>
        </Grid.Column>
        <Grid.Column width={9}>
          <Input
            fluid
            placeholder='Enter VIN'
            value={vin}
            onChange={handleChange}
            disabled={disabled || loading}
            data-testid='force-guarantee-vin-input'
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3} />
        <Grid.Column width={9}>
          <Button
            content='Search'
            primary
            onClick={handleSearch}
            disabled={disabled || loading || searchDisabled}
            loading={loading}
            data-testid='force-guarantee-search-button'
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
}

VinSearchForm.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.shape({
    productCode: PropTypes.string,
    displayName: PropTypes.string,
    description: PropTypes.string,
  })),
  productsLoading: PropTypes.bool,
  searchProductCode: PropTypes.string,
  searchVin: PropTypes.string,
  onSearch: PropTypes.func.isRequired,
};

VinSearchForm.defaultProps = {
  loading: false,
  disabled: false,
  products: [],
  productsLoading: false,
  searchProductCode: '',
  searchVin: '',
};

export default VinSearchForm;
